/**
 * Block Service
 * 
 * Service for handling block-related API calls.
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export interface Block {
  _id: string;
  id_client: string;
  callsign: string;
  nama: string;
  province?: string;
  kabkot?: string;
  reason?: string;
  blockedAt: string;
  blockedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BlockListResponse {
  blocks?: Block[];
  data?: Block[];
  nextCursor?: string | null;
  previousCursor?: string | null;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export interface BlockUserResponse {
  success: boolean;
  message: string;
  block?: Block;
}

export interface ValidateCallsignResponse {
  success: boolean;
  data?: {
    id_client: string;
    callsign: string;
    nama: string;
    province: string;
    kabkot: string;
  };
  error?: string;
}

class BlockService {
  /**
   * Fetch all blocks (legacy - fetches all)
   * @returns Promise with block list
   */
  async fetchBlocks(): Promise<BlockListResponse> {
    try {
      const url = `${apiConfig.baseURL}/api/block/list`;

      const response = await apiFetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching blocks:", error);
      throw error;
    }
  }

  /**
   * Fetch blocks with pagination and search
   */
  async fetchBlocksWithPagination(options: {
    cursor?: string;
    direction?: "next" | "previous";
    search?: string;
    sortBy?: "callsign" | "nama" | "province" | "blockedAt" | "_id";
    sortOrder?: "asc" | "desc";
  }): Promise<BlockListResponse> {
    try {
      const params = new URLSearchParams();
      if (options.cursor) params.set("cursor", options.cursor);
      if (options.direction) params.set("direction", options.direction);
      if (options.search) params.set("search", options.search);
      if (options.sortBy) params.set("sortBy", options.sortBy);
      if (options.sortOrder) params.set("sortOrder", options.sortOrder);

      const url = `${apiConfig.baseURL}/api/block/list?${params.toString()}`;

      const response = await apiFetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching blocks with pagination:", error);
      throw error;
    }
  }

  /**
   * Check if a user is blocked
   * @param callsign - Optional callsign to check
   * @param id_client - Optional id_client to check
   * @returns Promise with boolean indicating if blocked
   */
  async checkBlocked(callsign?: string, id_client?: string): Promise<boolean> {
    try {
      const params = new URLSearchParams();
      if (callsign) {
        params.set("callsign", callsign);
      }
      if (id_client) {
        params.set("id_client", id_client);
      }

      const url = `${apiConfig.baseURL}/api/block/check?${params.toString()}`;
      
      const response = await apiFetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.blocked;
    } catch (error) {
      console.error("Error checking if blocked:", error);
      throw error;
    }
  }

  /**
   * Block a user
   * @param options - Block options (id_client, callsign, nama, reason, blockedBy)
   * @returns Promise with block result
   */
  async blockUser(options: {
    id_client?: string;
    callsign?: string;
    nama?: string;
    reason?: string;
    blockedBy?: string;
  }): Promise<BlockUserResponse> {
    try {
      const response = await apiFetch(`${apiConfig.baseURL}/api/block`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(options),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error blocking user:", error);
      throw error;
    }
  }

  /**
   * Validate callsign via SDPPI
   * @param callsign - Callsign to validate
   * @returns Promise with validation result and user data from SDPPI
   */
  async validateCallsign(callsign: string): Promise<ValidateCallsignResponse> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/block/validate/${encodeURIComponent(callsign.toUpperCase())}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error validating callsign:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to validate callsign",
      };
    }
  }

  /**
   * Unblock a user
   * @param identifier - Callsign or id_client to unblock
   * @param by - Identifier type ("callsign" or "id_client")
   * @returns Promise with unblock result
   */
  async unblockUser(identifier: string, by: "callsign" | "id_client"): Promise<BlockUserResponse> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/block/unblock/${encodeURIComponent(identifier)}?by=${by}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error unblocking user:", error);
      throw error;
    }
  }
}

export const blockService = new BlockService();

